import React, { useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import { GlassCard } from '../GlassCard';
import { GlassOptionCard } from '../GlassOptionCard';

type GoalPhase = 'exploring' | 'planned' | 'executing';

interface PhaseStepProps {
  goalText: string;
  currentIndex: number;
  totalCount: number;
  onSelectPhase: (phase: GoalPhase) => void;
}

const PHASE_OPTIONS: Array<{
  value: GoalPhase;
  emoji: string;
  label: string;
  desc: string;
}> = [
  {
    value: 'exploring',
    emoji: '\uD83D\uDD0D',
    label: 'Still exploring',
    desc: 'Researching, collecting ideas, figuring it out',
  },
  {
    value: 'planned',
    emoji: '\uD83D\uDCCB',
    label: 'I have a plan',
    desc: "I know what to do, just need to organize it",
  },
  {
    value: 'executing',
    emoji: '\uD83D\uDE80',
    label: 'Already executing',
    desc: "In motion: need to track and stay focused",
  },
];

export function PhaseStep({
  goalText,
  currentIndex,
  totalCount,
  onSelectPhase,
}: PhaseStepProps) {
  const [selectedPhase, setSelectedPhase] = useState<GoalPhase | null>(null);

  const handleSelect = useCallback(
    (phase: GoalPhase) => {
      setSelectedPhase(phase);
      setTimeout(() => {
        onSelectPhase(phase);
      }, 400);
    },
    [onSelectPhase]
  );

  return (
    <GlassCard>
      <div className="flex flex-col">
        {/* Progress indicator */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex gap-1.5">
            {Array.from({ length: totalCount }).map((_, i) => (
              <motion.div
                key={i}
                className={`h-1 w-6 rounded-full transition-colors duration-300 ${i <= currentIndex ? 'bg-ink' : 'bg-line'}`}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.3, delay: i * 0.08 }}
                style={{ originX: 0 }}
              />
            ))}
          </div>
          <p
            className="text-sm"
            style={{ color: 'hsl(var(--ink-3))' }}
          >
            {currentIndex + 1} of {totalCount}
          </p>
        </div>

        <h1
          className="text-center font-display text-[28px] font-semibold text-ink tracking-tight"
        >
          Where are you at with "{goalText}"?
        </h1>

        <div className="mt-8 flex flex-col gap-3">
          {PHASE_OPTIONS.map((option, i) => (
            <motion.div
              key={option.value}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.15 + i * 0.08 }}
            >
              <GlassOptionCard
                selected={selectedPhase === option.value}
                onClick={() => handleSelect(option.value)}
                className="flex items-start gap-4"
              >
                <span className="text-2xl">{option.emoji}</span>
                <div>
                  <span className="block text-base font-medium text-ink">
                    {option.label}
                  </span>
                  <span
                    className="mt-0.5 block text-sm"
                    style={{ color: 'hsl(var(--ink-2))' }}
                  >
                    {option.desc}
                  </span>
                </div>
              </GlassOptionCard>
            </motion.div>
          ))}
        </div>
      </div>
    </GlassCard>
  );
}
